if (!customElements.get('timeline-section')) {
  customElements.define(
    'timeline-section',
    class TimelineSection extends HTMLElement {
      constructor() {
        super();
        this.track = this.querySelector('.timeline__track');
        this.prevButton = this.querySelector('.timeline__button--prev');
        this.nextButton = this.querySelector('.timeline__button--next');
        this.steps = Array.from(this.querySelectorAll('.timeline__step'));
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.frame = null;
        this.drag = null;

        this.update = this.update.bind(this);
        this.onScroll = this.onScroll.bind(this);
        this.onPointerDown = this.onPointerDown.bind(this);
        this.onPointerMove = this.onPointerMove.bind(this);
        this.onPointerUp = this.onPointerUp.bind(this);
        this.onCardMove = this.onCardMove.bind(this);
        this.onBlockSelect = this.onBlockSelect.bind(this);
      }

      connectedCallback() {
        if (!this.track) return;

        this.prevButton?.addEventListener('click', () => this.scrollByStep(-1));
        this.nextButton?.addEventListener('click', () => this.scrollByStep(1));
        this.track.addEventListener('scroll', this.onScroll, { passive: true });
        this.track.addEventListener('pointerdown', this.onPointerDown);

        this.querySelectorAll('.timeline__card').forEach((card) => {
          card.addEventListener('pointermove', this.onCardMove);
        });

        this.resizeObserver = new ResizeObserver(this.update);
        this.resizeObserver.observe(this.track);

        document.addEventListener('shopify:block:select', this.onBlockSelect);
        this.update();
      }

      disconnectedCallback() {
        this.resizeObserver?.disconnect();
        document.removeEventListener('shopify:block:select', this.onBlockSelect);
        if (this.frame) cancelAnimationFrame(this.frame);
      }

      get maxScroll() {
        return this.track.scrollWidth - this.track.clientWidth;
      }

      // Distance between two neighbouring steps
      get stepPitch() {
        if (this.steps.length < 2) return this.track.clientWidth;
        return this.steps[1].offsetLeft - this.steps[0].offsetLeft;
      }

      scrollByStep(direction) {
        const visibleSteps = Math.max(1, Math.floor(this.track.clientWidth / this.stepPitch) - 1);
        this.track.scrollBy({
          left: direction * visibleSteps * this.stepPitch,
          behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
        });
      }

      onScroll() {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.update();
        });
      }

      update() {
        const max = this.maxScroll;
        const left = this.track.scrollLeft;
        const progress = max > 0 ? left / max : 1;

        this.dataset.scrollable = max > 1 ? 'true' : 'false';
        this.style.setProperty('--timeline-progress', progress.toFixed(4));
        if (this.prevButton) this.prevButton.disabled = left <= 1;
        if (this.nextButton) this.nextButton.disabled = left >= max - 1;

        // Fill the line up to the dots left of the middle of the view
        const threshold = left >= max - 1 ? Infinity : left + this.track.clientWidth * 0.5;
        this.steps.forEach((step) => {
          const dot = step.querySelector('.timeline__dot');
          step.classList.toggle('is-passed', dot.offsetLeft + dot.offsetWidth / 2 <= threshold);
        });
      }

      // Mouse drag to scroll; touch and pen use native scrolling
      onPointerDown(event) {
        if (event.pointerType !== 'mouse' || event.button !== 0 || this.maxScroll <= 0) return;

        this.drag = { startX: event.clientX, startScroll: this.track.scrollLeft, moved: false, id: event.pointerId };
        this.track.addEventListener('pointermove', this.onPointerMove);
        this.track.addEventListener('pointerup', this.onPointerUp);
        this.track.addEventListener('pointercancel', this.onPointerUp);
      }

      onPointerMove(event) {
        if (!this.drag) return;
        const delta = event.clientX - this.drag.startX;

        if (!this.drag.moved && Math.abs(delta) > 4) {
          this.drag.moved = true;
          this.track.setPointerCapture(this.drag.id);
          this.classList.add('is-dragging');
        }
        if (this.drag.moved) this.track.scrollLeft = this.drag.startScroll - delta;
      }

      onPointerUp() {
        if (!this.drag) return;
        if (this.drag.moved) {
          this.track.releasePointerCapture(this.drag.id);
          // Restore snapping after the drag so the track settles on a step
          const left = this.track.scrollLeft;
          this.classList.remove('is-dragging');
          this.track.scrollLeft = left;
        }

        this.drag = null;
        this.track.removeEventListener('pointermove', this.onPointerMove);
        this.track.removeEventListener('pointerup', this.onPointerUp);
        this.track.removeEventListener('pointercancel', this.onPointerUp);
      }

      // Cursor spotlight
      onCardMove(event) {
        if (event.pointerType !== 'mouse' || this.drag?.moved) return;

        const card = event.currentTarget;
        const rect = card.getBoundingClientRect();
        card.style.setProperty('--timeline-mx', `${(((event.clientX - rect.left) / rect.width) * 100).toFixed(1)}%`);
        card.style.setProperty('--timeline-my', `${(((event.clientY - rect.top) / rect.height) * 100).toFixed(1)}%`);
      }

      onBlockSelect(event) {
        if (!this.contains(event.target)) return;
        this.track.scrollTo({
          left: event.target.offsetLeft - (this.track.clientWidth - event.target.offsetWidth) / 2,
          behavior: 'smooth',
        });
      }
    }
  );
}
